'use client';

import { Box, Theme, Typography } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import type { TSelectableEntityType } from '../../EntityTypeSelector/EntityTypeSelector/entityTypeGroups';
import { toNewIdentityId } from '../IdentityTypeRow/IdentityTypeRow';
import { groupUnheldTypes } from './groupUnheldTypes';
import { RequestEntityTile } from './RequestEntityTile';
import { useRovingRadioGroup } from './useRovingRadioGroup';

/** The id the "just me" tile reports and is chosen by: the Individual choice itself. */
const JUST_ME_ID = 'individual';

type NewTypeTilesProps = {
  unheldTypes: readonly { entityType: TSelectableEntityType }[];
  /**
   * True to put the "just me" tile (the Individual tile) first in the group, so it is reached with
   * the same arrow keys and tab stop as the others (verifime-adr#709). It carries no group heading.
   */
  offersJustMe: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** The accessible name of the group of tiles. */
  label: string;
  /**
   * The level of the group headings (Organisation, Trust, Business): one under the heading that
   * comes before the tiles, so heading levels never skip.
   */
  groupHeadingLevel: 3 | 4;
};

// One column below `md`, three from `md` up (D12).
const tileGridSx = (theme: Theme) => ({
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
  gap: theme.semantic.spacing.base,
});

/**
 * The tiles for the entity types the customer holds none of, under Organisation, Trust and
 * Business headings (a single tile has no heading). All the tiles are one radio group, with one tab
 * stop. A tile reports `new:<entityType>`, the same id the new-one card in a row reports. When
 * "just me" is offered, its tile joins the group as the first radio and reports `individual`.
 */
export function NewTypeTiles({
  unheldTypes,
  offersJustMe,
  selectedId,
  onSelect,
  label,
  groupHeadingLevel,
}: Readonly<NewTypeTilesProps>): ReactElement {
  const groups = useMemo(() => groupUnheldTypes(unheldTypes), [unheldTypes]);
  const tileTypes = groups.flatMap((group) => group.entityTypes);
  const showGroupTitles = tileTypes.length > 1;
  const justMeIds = offersJustMe ? [JUST_ME_ID] : [];
  const items = [...justMeIds, ...tileTypes.map(toNewIdentityId)].map((id) => ({
    id,
    unavailable: false,
  }));
  const { groupHandlers, tabIndexOf } = useRovingRadioGroup({ items, selectedId });
  // The tile types follow the "just me" tile in the group, so their positions shift by one.
  const positionOf = (entityType: TSelectableEntityType) =>
    justMeIds.length + tileTypes.indexOf(entityType);

  return (
    <Box
      role="radiogroup"
      aria-label={label}
      {...groupHandlers}
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.semantic.spacing['2lg'],
      })}
    >
      {offersJustMe && (
        <Box sx={tileGridSx}>
          <RequestEntityTile
            entityType="individual"
            selected={selectedId === JUST_ME_ID}
            onSelect={() => onSelect(JUST_ME_ID)}
            tabIndex={tabIndexOf(0)}
          />
        </Box>
      )}
      {groups.map((group) => (
        <Box
          key={group.title}
          sx={(theme: Theme) => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.semantic.spacing.base,
          })}
        >
          {showGroupTitles && (
            <Typography variant="h5" component={`h${groupHeadingLevel}`}>
              {group.title}
            </Typography>
          )}
          <Box sx={tileGridSx}>
            {group.entityTypes.map((entityType) => (
              <RequestEntityTile
                key={entityType}
                entityType={entityType}
                selected={selectedId === toNewIdentityId(entityType)}
                onSelect={() => onSelect(toNewIdentityId(entityType))}
                tabIndex={tabIndexOf(positionOf(entityType))}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
